// src/modules/app-views/invoices/pages/Init/modals/FiscalBootstrapModal/index.jsx
// *******************************************************************************
// FISCAL BOOTSTRAP MODAL
// *******************************************************************************
// Modal that guides users through fiscal configuration when entering invoices
// module for the first time. Ensures company has certificate and NFe/NFCe enabled.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SettingOutlined, HomeOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import app from 'configs/appResolver';
import step1 from './Step1';
import step2 from './Step2';
import step3 from './Step3';
import step4 from './Step4';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// Resource UIDs from subscription that control NFe/NFCe access
const RESOURCE_NFE = 'moduleresources_nfe';
const RESOURCE_NFCE = 'moduleresources_nfce';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const FiscalBootstrapModal = ({ show, setShow, afterClose, company, subscription }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedCompany = useZustand('auth', 'company');
  const storedSubscription = useZustand('auth', 'subscription');

  // Use prop subscription or fallback to stored subscription
  const currentSubscription = subscription || storedSubscription;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const navigate = useNavigate();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Use prop company or fallback to stored company
  const currentCompany = company || storedCompany;
  const entityId = currentCompany?.entityId || currentCompany?._id;

  // Check subscription resources to determine which fiscal services are available
  const subscriptionResources = currentSubscription?.resources || [];
  const hasNfeResource = subscriptionResources.includes(RESOURCE_NFE);
  const hasNfceResource = subscriptionResources.includes(RESOURCE_NFCE);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // State for tracking completion of each step
  const [state, setState] = useState({
    certificateUploaded: false,
    fiscalDataSaved: false,
    nfeEnabled: false,
    nfceEnabled: false,
  });

  // Local state for certificate and fiscal data (updated during modal flow)
  const [localCertificate, setLocalCertificate] = useState(null);

  // State registration data retrieved after certificate upload (from CCC/SEFAZ)
  const [localStateRegistration, setLocalStateRegistration] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Initialize state from company data
  useEffect(() => {
    if (currentCompany) {
      const hasCertificate = !!(
        currentCompany?.fiscal?.certificate ||
        currentCompany?.fiscalData?.certificate
      );
      const nfeEnabled = currentCompany?.fiscalData?.nfe?.isEnabled || false;
      const nfceEnabled = currentCompany?.fiscalData?.nfce?.isEnabled || false;

      setState(prev => ({
        ...prev,
        certificateUploaded: hasCertificate,
        nfeEnabled,
        nfceEnabled,
      }));

      if (hasCertificate) {
        setLocalCertificate(
          currentCompany?.fiscal?.certificate || currentCompany?.fiscalData?.certificate
        );
      }
    }
  }, [currentCompany]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleBackToHome = useCallback(() => {
    setShow(false);
    navigate(app.AUTHENTICATED_ENTRY);
  }, [navigate, setShow]);

  const handleCertificateSuccess = useCallback(({ certificateData, stateRegistrationData }) => {
    setLocalCertificate(certificateData);
    setState(prev => ({ ...prev, certificateUploaded: true }));

    // Store state registration data for Step2
    if (stateRegistrationData) {
      setLocalStateRegistration(stateRegistrationData);
    }
  }, []);

  const handleNfeEnabled = useCallback(enabled => {
    setState(prev => ({ ...prev, nfeEnabled: enabled }));
  }, []);

  const handleNfceEnabled = useCallback(enabled => {
    setState(prev => ({ ...prev, nfceEnabled: enabled }));
  }, []);

  const handleFinish = useCallback(async () => {
    // Check if all required services based on subscription resources are enabled
    const nfeOk = !hasNfeResource || state.nfeEnabled;
    const nfceOk = !hasNfceResource || state.nfceEnabled;

    if (nfeOk && nfceOk) {
      return true; // Close modal
    }
    return false; // Keep modal open
  }, [hasNfeResource, hasNfceResource, state.nfeEnabled, state.nfceEnabled]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Determine which step is the confirm step (last visible step)
  // If both resources: NFCe (step4) is confirm
  // If only NFe: NFe (step3) is confirm
  // If only NFCe: NFCe (step4) is confirm (but it appears after step2)
  const nfeIsConfirmStep = hasNfeResource && !hasNfceResource;

  const steps = useMemo(
    () => [
      step1({
        entityId,
        company: currentCompany,
        localCertificate,
        onCertificateSuccess: handleCertificateSuccess,
      }),
      step2({
        company: currentCompany,
        localCertificate,
        localStateRegistration,
      }),
      step3({
        entityId,
        company: currentCompany,
        nfeEnabled: state.nfeEnabled,
        onNfeEnabled: handleNfeEnabled,
        hidden: !hasNfeResource,
        isConfirmStep: nfeIsConfirmStep,
      }),
      step4({
        entityId,
        company: currentCompany,
        nfceEnabled: state.nfceEnabled,
        onNfceEnabled: handleNfceEnabled,
        hidden: !hasNfceResource,
      }),
    ],
    [
      entityId,
      currentCompany,
      localCertificate,
      localStateRegistration,
      state.nfeEnabled,
      state.nfceEnabled,
      hasNfeResource,
      hasNfceResource,
      nfeIsConfirmStep,
      handleCertificateSuccess,
      handleNfeEnabled,
      handleNfceEnabled,
    ]
  );

  // Left button - Back to Home
  const customLeftButtons = useMemo(
    () => [
      {
        label: 'back_to_home',
        onClick: handleBackToHome,
        type: 'default',
        icon: <HomeOutlined />,
      },
    ],
    [handleBackToHome]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<SettingOutlined />}
      title="fiscal_configuration_required"
      steps={steps}
      onFinish={handleFinish}
      afterClose={afterClose}
      width={720}
      maskClosable={false}
      closable={false}
      customLeftButtons={customLeftButtons}
    />
  );
};

// *******************************************************************************
// HELPER - Check if company needs fiscal configuration
// *******************************************************************************
/**
 * Checks if the company needs fiscal bootstrap configuration
 * @param {Object} company - Company data from Zustand
 * @param {Object} subscription - Subscription data from Zustand (optional)
 * @returns {boolean} - True if fiscal configuration is needed
 */
export const needsFiscalBootstrap = (company, subscription) => {
  if (!company) return false;

  // Get subscription resources (if not provided, assume both are needed for backwards compatibility)
  const resources = subscription?.resources || [];
  const hasNfeResource = resources.length === 0 || resources.includes(RESOURCE_NFE);
  const hasNfceResource = resources.length === 0 || resources.includes(RESOURCE_NFCE);

  // If no fiscal resources in subscription, no bootstrap needed
  if (!hasNfeResource && !hasNfceResource) return false;

  // Check if has valid certificate
  const hasCertificate = !!(company?.fiscal?.certificate || company?.fiscalData?.certificate);

  // If no certificate, needs bootstrap
  if (!hasCertificate) return true;

  // Check if NFe is enabled (only if resource is in subscription)
  const nfeEnabled = company?.fiscalData?.nfe?.isEnabled || false;
  if (hasNfeResource && !nfeEnabled) return true;

  // Check if NFCe is enabled (only if resource is in subscription)
  const nfceEnabled = company?.fiscalData?.nfce?.isEnabled || false;
  if (hasNfceResource && !nfceEnabled) return true;

  return false;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FiscalBootstrapModal;
